<template>
  <div id="retailer-management-for-agents" class="fill-height align-start">
    <i-overlay
      v-if="fetching && $vuetifyDisplay.smAndDown"
      text="`Loading ${isILR ? 'Customer' : 'Retailer'} Invites data...`"
    />

    <v-row v-else class="g-container" no-gutters>
      <i-create-retailer-invite
        v-model="retailerInviteDialog"
        @cancel="cancelRetailerInvite"
        @confirm="confirmRetailerInvite"
      />

      <v-dialog
        v-if="dialogConfirmation"
        v-model="dialogConfirmation"
        width="400px"
        @click:outside="cancelDialog"
        @keydown.esc="cancelDialog"
      >
        <i-confirmation-dialog
          :text="dialogText"
          :action-text="dialogActionText"
          :submitting="updating"
          @cancel="cancelDialog"
          @confirm="confirmDialog"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0 mb-4">{{ $route.meta.title }}</h2>

        <v-row>
          <template v-if="userIsAllowedToEdit">
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="retailerInviteDialog = true"
              >
                Invite {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
              </v-btn>
            </v-col>
          </template>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="account/retailer-turn-in-orders/retailers"
            ></i-instructions>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="4" md="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': inAcceptedView }"
              @click="selectedStatus = INVITE_STATUSES.ACCEPTED"
              >Accepted</v-btn
            >
          </v-col>
          <v-col cols="4" md="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': inPendingView }"
              @click="selectedStatus = INVITE_STATUSES.PENDING"
            >
              Pending
              <v-badge
                overlap
                class="ml-1"
                color="orange"
                v-if="pendingInvitations"
                :content="pendingInvitations"
              >
              </v-badge>
            </v-btn>
          </v-col>
          <v-col cols="4" md="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': inCancelledView }"
              @click="selectedStatus = INVITE_STATUSES.CANCELLED"
              >Cancelled</v-btn
            >
          </v-col>
          <v-col cols="12" md="6">
            <i-local-filter
              :filterPlaceholder="`Search by ${isILR ? 'Customer' : 'Retailer'}`"
              @filter="localFilter"
            />
          </v-col>
        </v-row>

        <template>
          <v-row>
            <v-col cols="12">
              <v-col
                class="pa-0"
                v-if="!fetching && filteredInvites.length === 0"
              >
                <i-empty-results
                  :buttonText="`${isILR ? 'Customer' : 'Retailer'} Turn In Orders`"
                  buttonRouteName="retailer-turn-in-orders"
                  :title="`No ${selectedStatus.toLowerCase()} ${isILR ? 'Customer' : 'Retailer'} Invites found`"
                />
              </v-col>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <v-col
                    class="mt-0 pa-0"
                    v-for="(item, index) in filteredInvites"
                    :key="index"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col class="py-1 g-bold">
                          <span>{{ headers[1].text }}</span>
                          <br />
                          <span>{{ item.store.displayName }}</span>
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[0].text }}</span>
                        </v-col>
                        <v-col cols="6" class="pl-4">
                          <span>{{
                            $filters.formatDateTime12h(item.initiatedDate)
                          }}</span>
                        </v-col>
                      </v-row>

                      <v-row no-gutters v-if="inPendingView || inCancelledView">
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            small
                            class="i-tag-secondary"
                            v-if="item.initiatedByType === 'STORE'"
                            ><strong>{{
                              isILR ? 'CUSTOMER' : 'RETAILER'
                            }}</strong></v-chip
                          >
                          <v-chip
                            small
                            class="i-tag-primary"
                            v-else-if="item.initiatedByType === 'SUPPLIER'"
                            ><strong>SUPPLIER</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-row no-gutters v-if="inCancelledView">
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[3].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            small
                            class="i-tag-error"
                            v-if="item.cancelledByType === 'STORE'"
                            ><strong>{{
                              isILR ? 'CUSTOMER' : 'RETAILER'
                            }}</strong></v-chip
                          >
                          <v-chip
                            small
                            class="i-tag-error"
                            v-else-if="item.cancelledByType === 'SUPPLIER'"
                            ><strong>SUPPLIER</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <template v-if="userIsAllowedToEdit">
                        <v-divider
                          v-if="inAcceptedView || inPendingView"
                          class="my-2"
                        ></v-divider>

                        <v-row
                          v-if="inAcceptedView || inPendingView"
                          no-gutters
                        >
                          <v-col
                            v-if="
                              inPendingView && item.initiatedByType === 'STORE'
                            "
                            cols="12"
                            class="py-1"
                          >
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              :disabled="fetching || updating"
                              @click="handleAcceptInvite(item)"
                            >
                              <span>Accept invitation</span>
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="fetching || updating"
                              @click="handleDeleteInvite(item)"
                            >
                              <span>Delete invitation</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </v-col>
                </template>

                <v-data-table
                  v-else
                  class="pt-2 elevation-1 i-data-table"
                  hide-default-footer
                  item-key="store.name"
                  :headers="filteredHeaders"
                  :items="filteredInvites"
                  :items-per-page="-1"
                  :loading="fetching || updating"
                >
                  <template #[`item.initiatedDate`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.initiatedDate)
                    }}</span>
                  </template>

                  <template #[`item.initiatedByType`]="{ item }">
                    <v-chip
                      small
                      class="i-tag-secondary"
                      v-if="item.initiatedByType === 'STORE'"
                      ><strong>{{
                        isILR ? 'CUSTOMER' : 'RETAILER'
                      }}</strong></v-chip
                    >
                    <v-chip
                      small
                      class="i-tag-primary"
                      v-else-if="item.initiatedByType === 'SUPPLIER'"
                      ><strong>SUPPLIER</strong></v-chip
                    >
                  </template>

                  <template #[`item.cancelledByType`]="{ item }">
                    <v-chip
                      small
                      class="i-tag-error"
                      v-if="item.cancelledByType === 'STORE'"
                      ><strong>{{
                        isILR ? 'CUSTOMER' : 'RETAILER'
                      }}</strong></v-chip
                    >
                    <v-chip
                      small
                      class="i-tag-error"
                      v-else-if="item.cancelledByType === 'SUPPLIER'"
                      ><strong>SUPPLIER</strong></v-chip
                    >
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <v-btn
                      v-if="inPendingView && item.initiatedByType === 'STORE'"
                      rounded
                      color="secondary"
                      class="mx-1"
                      :disabled="!userIsAllowedToEdit || fetching || updating"
                      v-tooltip="{
                        content: `Accept ${isILR ? 'Customer' : 'Retailer'} Invite`,
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="handleAcceptInvite(item)"
                    >
                      Accept
                    </v-btn>

                    <v-btn
                      icon
                      color="error"
                      class="i-icon-red mx-1"
                      :disabled="!userIsAllowedToEdit || fetching || updating"
                      v-tooltip="{
                        content: `Delete ${isILR ? 'Customer' : 'Retailer'} Invite`,
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="handleDeleteInvite(item)"
                    >
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DataTables from '@/utils/DataTables'
import iInstructions from '@/components/Instructions'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iCreateRetailerInvite from '../components/CreateRetailerInvite'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iEmptyResults from '@/components/EmptyResults'

const INVITE_STATUSES = {
  ACCEPTED: 'ACCEPTED',
  CANCELLED: 'CANCELLED',
  PENDING: 'PENDING'
}

const INVITE_ACTIONS = {
  CONFIRM: 'CONFIRM',
  DELETE: 'DELETE'
}

const TIMEOUT_DURATION = 1000
const VISIBLE_TABLE_ROWS = 10

export default {
  name: 'retailer-turn-in-orders',

  props: ['status'],

  components: {
    iEmptyResults,
    iInstructions,
    iConfirmationDialog,
    iCreateRetailerInvite,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    filterText: '',
    retailerInviteDialog: false,
    dialogAction: null,
    dialogActionText: '',
    dialogConfirmation: false,
    dialogText: '',
    tableHeight: 'auto',
    tableTimeout: null,
    selectedInvitation: null,
    selectedStatus: INVITE_STATUSES.ACCEPTED,
    INVITE_STATUSES
  }),

  computed: {
    ...mapGetters('preferences', ['isOwnerUser', 'isAdminUser']),
    ...mapGetters('tenant', ['isILR']),
    ...mapState('retailerInvites', ['fetching', 'updating', 'agencyConsents']),

    headers() {
      return [
        {
          text: 'DATE',
          value: 'initiatedDate',
          visibleStatuses: [
            INVITE_STATUSES.ACCEPTED,
            INVITE_STATUSES.CANCELLED,
            INVITE_STATUSES.PENDING
          ],
          width: '25%'
        },
        {
          text: this.isILR ? 'CUSTOMER' : 'RETAILER',
          value: 'store.displayName',
          visibleStatuses: [
            INVITE_STATUSES.ACCEPTED,
            INVITE_STATUSES.CANCELLED,
            INVITE_STATUSES.PENDING
          ]
        },
        {
          align: 'center',
          text: 'INITIATED BY',
          value: 'initiatedByType',
          visibleStatuses: [INVITE_STATUSES.CANCELLED, INVITE_STATUSES.PENDING],
          width: '20%'
        },
        {
          align: 'center',
          text: 'CANCELLED BY',
          value: 'cancelledByType',
          visibleStatuses: [INVITE_STATUSES.CANCELLED],
          width: '20%'
        },
        {
          align: 'center',
          text: 'ACTIONS',
          value: 'actions',
          sortable: false,
          visibleStatuses: [INVITE_STATUSES.ACCEPTED, INVITE_STATUSES.PENDING],
          width: '20%'
        }
      ]
    },

    invitesByStatus() {
      return this.agencyConsents.filter(
        (consent) => consent.status === this.selectedStatus
      )
    },

    filteredInvites() {
      return this.invitesByStatus.filter((consent) =>
        consent.store.name.toLowerCase().includes(this.filterText.toLowerCase())
      )
    },

    filteredHeaders() {
      return this.headers.filter((header) =>
        header.visibleStatuses.includes(this.selectedStatus)
      )
    },

    inAcceptedView() {
      return this.selectedStatus === INVITE_STATUSES.ACCEPTED
    },

    inCancelledView() {
      return this.selectedStatus === INVITE_STATUSES.CANCELLED
    },

    inPendingView() {
      return this.selectedStatus === INVITE_STATUSES.PENDING
    },

    isOverMaximumVisibleRows() {
      return this.filteredInvites.length > VISIBLE_TABLE_ROWS
    },

    pendingInvitations() {
      return this.agencyConsents?.filter(
        (consent) => consent.status === INVITE_STATUSES.PENDING
      )?.length
    },

    userIsAllowedToEdit() {
      return this.isOwnerUser || this.isAdminUser
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('retailerInvites', [
      'acceptAgencyConsent',
      'cancelAgencyConsent',
      'createAgencyConsent',
      'fetchAgencyConsents'
    ]),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    setTableSize() {
      if (!this.isOverMaximumVisibleRows) {
        this.tableHeight = 'auto'
        return
      }

      // Timeout needed to let UI fully render before calculating values
      clearTimeout(this.tableTimeout)
      this.tableTimeout = setTimeout(() => {
        this.tableHeight = DataTables.calculateTableHeight(VISIBLE_TABLE_ROWS)
      }, TIMEOUT_DURATION)
    },

    // ***
    // Create invitations actions
    // ***

    cancelRetailerInvite() {
      this.retailerInviteDialog = false
    },

    async confirmRetailerInvite(storeIds) {
      await this.createAgencyConsent(storeIds)
      this.retailerInviteDialog = false
    },

    // ***
    // Received invitations actions
    // ***

    handleAcceptInvite(item) {
      this.selectedInvitation = item
      this.dialogAction = INVITE_ACTIONS.CONFIRM
      this.dialogText = 'Do you want to accept this invitation?'
      this.dialogActionText = 'Accept'
      this.dialogConfirmation = true
    },

    handleDeleteInvite(item) {
      this.selectedInvitation = item
      this.dialogAction = INVITE_ACTIONS.DELETE
      this.dialogText = 'Remove access to Agent Turn In Orders?'
      this.dialogActionText = 'Delete'
      this.dialogConfirmation = true
    },

    cancelDialog() {
      this.selectedInvitation = null
      this.dialogConfirmation = false
      this.dialogAction = null
      this.dialogText = ''
      this.dialogActionText = ''
    },

    async confirmDialog() {
      if (!this.dialogAction || !this.selectedInvitation) return

      if (this.dialogAction === INVITE_ACTIONS.CONFIRM) {
        await this.acceptAgencyConsent(this.selectedInvitation.id)
        this.cancelDialog()
        return
      }
      if (this.dialogAction === INVITE_ACTIONS.DELETE) {
        await this.cancelAgencyConsent(this.selectedInvitation.id)
        this.cancelDialog()
      }
    }
  },

  async mounted() {
    if (this.status) this.selectedStatus = this.status
    await this.fetchAgencyConsents()
    this.setTableSize()
  },

  created() {
    window.addEventListener('resize', this.setTableSize)
  },

  unmounted() {
    window.removeEventListener('resize', this.setTableSize)
  },

  watch: {
    'filteredInvites.length'() {
      this.tableHeight = 'auto'
      this.setTableSize()
    }
  }
}
</script>

<style lang="scss" scoped></style>
